import { useCallback, useEffect, useRef, useState } from "react";

/**
 * IntersectionObserver-based reveal hook.
 * Returns `{ ref, inView }`. `ref` is a callback ref — it (re)attaches the
 * observer whenever the underlying DOM node is mounted, so it works even when
 * the consuming element is conditionally rendered after async data loads.
 */
export default function useReveal(threshold = 0.15) {
  const [inView, setInView] = useState(false);
  const observerRef = useRef(null);
  const triggeredRef = useRef(false);

  const ref = useCallback(
    (node) => {
      // Disconnect any previous observer (re-attach scenario).
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (!node || triggeredRef.current) return;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              triggeredRef.current = true;
              setInView(true);
              if (observerRef.current) {
                observerRef.current.unobserve(entry.target);
              }
            }
          });
        },
        { threshold, rootMargin: "0px 0px -60px 0px" }
      );
      observerRef.current.observe(node);
    },
    [threshold]
  );

  useEffect(
    () => () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    },
    []
  );

  return { ref, inView };
}
